<template>
  <SignupStepFrame
    :step-number="4"
    :step-total="5"
    eyebrow="Step 4 — Your rewards"
    title="Choose the perks your team can <b>earn</b>."
    subtitle="Team experiences, perks, and donations your team can redeem with the gems they earn from recognition. Uncheck anything you don&#39;t want."
    :can-go-next="canGoNext"
    @next="onNext"
    @back="onBack"
    @saveAndExit="onSaveAndExit"
  >
    <div v-if="loadingRewards" class="rewards-step__skeleton">
      <div v-for="i in 6" :key="i" class="rewards-step__skeleton-card" />
    </div>

    <RewardChecklist
      v-else
      :rewards="filteredRewardList"
      :selected-ids="selectedRewardIds"
      @input="onSelectionChanged"
    />

    <p
      v-if="!loadingRewards && companyCountry && filteredRewardList.length === 0"
      class="rewards-step__empty-hint"
    >
      No perks found for <strong>{{ companyCountry }}</strong> yet. You can still continue and
      add your own from <strong>Settings → Rewards</strong>.
    </p>

    <p class="rewards-step__custom-hint">
      <span class="rewards-step__custom-hint-icon" aria-hidden="true">✨</span>
      You can add your own custom rewards anytime from
      <strong>Settings → Rewards</strong>.
    </p>

    <p class="rewards-step__giftcard-hint">
      <span class="rewards-step__giftcard-icon" aria-hidden="true">🎁</span>
      Want to offer gift cards (Apple, DoorDash, and more)? Those become available
      when you upgrade — you'll top up reward credits and we handle delivery.
    </p>

    <p v-if="loadError" class="signup-error">
      We couldn&#39;t load the rewards catalog. You can still continue — add rewards later in Settings.
    </p>
  </SignupStepFrame>
</template>

<script>
/**
 * WizardRewardsStep (step 4) — perks-only catalog.
 *
 * Loads the global rewards template via `register/rewardMaster` which
 * reads `/rewards where company='global'` from Firebase RTDB. The
 * register store exposes `state.register.rewardMaster` as a keyed
 * object: `{ <pushKey>: {reward_en, img, filepath, tag, coins, gems, ...} }`.
 *
 * Scope: signup intentionally only surfaces non-gift-card rewards
 * (perks, experiences, donations). Gift cards (`tag[0] === '0'`) require
 * a funded reward-credit balance for delivery, which is paid-tier only —
 * users can enable gift cards by country and top up credits after
 * upgrading. This keeps signup free of payment intent collection.
 *
 * Pre-selection heuristic: if `selectedRewardIds` is empty on first
 * visit, pre-check the first ~8 perks from the master list — placeholder
 * until a real popularity signal exists.
 *
 * Country filter mirrors the authed rewards view
 * (`src/store/reward.ts:612-637`): a record's `country` is an ISO-2 code;
 * `""` (or missing) and the sentinel `"All Countries"` mean "show
 * everywhere". Otherwise we only show rewards whose country matches
 * `wizardData.company.country`. When the user steps back and switches
 * country, we prune any selected IDs that fall out of the new filter so
 * the payload doesn't carry rewards the user can't see.
 */
import SignupStepFrame from './SignupStepFrame.vue'
import RewardChecklist from './_primitives/RewardChecklist.vue'
import { mapGetters } from 'vuex'

const POPULAR_SUBSET_SIZE = 8

/**
 * Gift cards are signaled by `tag[0] === '0'` in the catalog. We
 * exclude them from signup; everything else is considered a "perk" for
 * Step 4's purposes.
 */
function isGiftCard(reward) {
  const tag = reward && reward.tag
  const first =
    Array.isArray(tag) && tag.length ? String(tag[0]) :
    (typeof tag === 'string' || typeof tag === 'number') ? String(tag) : ''
  return first === '0'
}

export default {
  name: 'WizardRewardsStep',
  components: { SignupStepFrame, RewardChecklist },
  data() {
    return {
      loadingRewards: false,
      loadError: null,
    }
  },
  computed: {
    ...mapGetters('signup', ['selectedRewardIds']),
    rewardMaster() {
      const raw = this.$store.state.register && this.$store.state.register.rewardMaster
      return raw || {}
    },
    rewardList() {
      const raw = this.rewardMaster
      if (!raw) return []
      if (Array.isArray(raw)) {
        return raw.filter(Boolean).map((r, i) => ({
          ...r,
          key: r.key || r.id || String(i),
        }))
      }
      return Object.keys(raw).map((k) => ({
        ...raw[k],
        key: raw[k].key || k,
      }))
    },
    /** ISO-2 country code chosen on Step 1, uppercased; '' if unset. */
    companyCountry() {
      const wd = this.$store.state.signup && this.$store.state.signup.wizardData
      const c = wd && wd.company && wd.company.country
      return String(c || '').trim().toUpperCase()
    },
    /**
     * Reward catalog narrowed to perks (no gift cards) and the
     * company's country. Country semantics mirror the authed view:
     * missing country and the "All Countries" sentinel are universal.
     */
    filteredRewardList() {
      const country = this.companyCountry
      return this.rewardList.filter((r) => {
        if (isGiftCard(r)) return false
        if (!country) return true
        const rc = r && r.country
        if (!rc) return true
        const upper = String(rc).trim().toUpperCase()
        if (!upper) return true
        if (upper === 'ALL COUNTRIES') return true
        return upper === country
      })
    },
    canGoNext() {
      // Step is optional: zero rewards is allowed.
      return true
    },
  },
  async created() {
    await this.ensureRewardsLoaded()
    this.seedDefaultSelectionIfEmpty()
    this.pruneSelectionToFilter()
  },
  watch: {
    /**
     * If the user steps back to Step 1, switches country, and returns to
     * Step 4, drop any selected reward IDs that are no longer visible —
     * we don't want to ship a payload that includes rewards the user
     * can't see (and that backend would reject for the new country).
     */
    companyCountry() {
      if (!this.rewardList.length) return
      this.pruneSelectionToFilter()
      // If they wiped their selection by switching countries, reseed so
      // the step still has a sensible default.
      if (Array.isArray(this.selectedRewardIds) && this.selectedRewardIds.length === 0) {
        this.seedDefaultSelectionIfEmpty()
      }
    },
  },
  methods: {
    async ensureRewardsLoaded() {
      const alreadyLoaded = this.rewardList.length > 0
      if (alreadyLoaded) return
      this.loadingRewards = true
      this.loadError = null
      try {
        await this.$store.dispatch('register/rewardMaster')
      } catch (err) {
        this.loadError = (err && err.message) || 'Failed to load rewards'
      } finally {
        this.loadingRewards = false
      }
    },
    seedDefaultSelectionIfEmpty() {
      if (Array.isArray(this.selectedRewardIds) && this.selectedRewardIds.length > 0) {
        return
      }
      const list = this.filteredRewardList
      if (!list.length) return
      const seed = list
        .slice(0, POPULAR_SUBSET_SIZE)
        .map((r) => r.key)
        .filter(Boolean)
      this.$store.commit('signup/SET_SELECTED_REWARDS', seed)
    },
    /**
     * Drop any selected IDs that the current country filter hides. Run
     * on mount and after a country change.
     */
    pruneSelectionToFilter() {
      const ids = Array.isArray(this.selectedRewardIds) ? this.selectedRewardIds : []
      if (!ids.length) return
      const visible = Object.create(null)
      for (const r of this.filteredRewardList) {
        if (r && r.key) visible[r.key] = true
      }
      const next = ids.filter((id) => visible[id])
      if (next.length !== ids.length) {
        this.$store.commit('signup/SET_SELECTED_REWARDS', next)
      }
    },
    onSelectionChanged(nextIds) {
      this.$store.commit('signup/SET_SELECTED_REWARDS', Array.isArray(nextIds) ? nextIds : [])
    },
    onNext() {
      this.$emit('next')
    },
    onBack() {
      this.$emit('back')
    },
    onSaveAndExit() {
      this.$emit('saveAndExit')
    },
  },
}
</script>

<style lang="scss" scoped>
@import './_shared.scss';

.rewards-step__skeleton {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;

  @media (min-width: 640px) { grid-template-columns: 1fr 1fr; }
}

.rewards-step__skeleton-card {
  height: 88px;
  border-radius: 14px;
  border: 1px solid var(--signup-border-soft);
  background: linear-gradient(
    90deg,
    var(--signup-surface-subtle) 0%,
    #f1edf8 50%,
    var(--signup-surface-subtle) 100%
  );
  background-size: 200% 100%;
  animation: rewards-shimmer 1200ms ease-in-out infinite;
}

.rewards-step__empty-hint {
  margin: 16px 0 4px;
  padding: 12px 14px;
  border: 1px dashed var(--signup-border-soft);
  border-radius: 12px;
  background: var(--signup-surface-subtle);
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 13px;
  line-height: 1.5;
  color: var(--signup-text-soft);

  strong {
    font-weight: 800;
    color: var(--signup-text);
  }
}

.rewards-step__giftcard-hint {
  margin: 10px 0 0;
  padding: 10px 12px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  border-radius: 10px;
  background: rgba(247, 185, 36, 0.10);
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--signup-text-soft);

  strong {
    font-weight: 800;
    color: var(--signup-text);
  }
}
.rewards-step__giftcard-icon {
  flex-shrink: 0;
  font-size: 14px;
  line-height: 1.4;
}

.rewards-step__custom-hint {
  margin: 14px 0 0;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--signup-text-muted);
  letter-spacing: 0.01em;

  strong {
    font-weight: 800;
    color: var(--signup-text-soft);
  }
}

.rewards-step__custom-hint-icon {
  font-size: 13px;
  line-height: 1;
}

@keyframes rewards-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
